import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ClipboardList, Contact, Package, Send } from 'lucide-react';
import { FACILITY_REQUEST_DRAFT_DAYS, facilityTypeForPlans, type FacilityRequestBody, type FacilityRequestDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PlanPicker, type PickablePlan } from './PlanPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const EMPTY_REQUEST: FacilityRequestBody = {
  name: '',
  facilityType: 'hospital',
  environment: 'live',
  contactName: null,
  contactEmail: null,
  contactPhone: null,
  planIds: [],
  addOns: [],
  offlineEnabled: false,
  template: null,
  notes: null,
  details: {},
};

export function FacilityRequestFormPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/owner/facility-requests');
  const [form, setForm] = useState<FacilityRequestBody>(EMPTY_REQUEST);
  const [submitNow, setSubmitNow] = useState(true);
  const set = <K extends keyof FacilityRequestBody>(key: K, value: FacilityRequestBody[K]) => setForm((current) => ({ ...current, [key]: value }));

  const plans = useQuery({ queryKey: ['plans'], queryFn: () => get<PickablePlan[]>('/platform/plans') });

  const save = useMutation({
    mutationFn: async () => {
      const chosen = (plans.data ?? []).filter((plan) => form.planIds.includes(plan.id));
      const created = await post<FacilityRequestDto>('/platform/facility-requests', {
        ...form,
        facilityType: facilityTypeForPlans(chosen.map((plan) => plan.facilityType)),
        contactName: form.contactName?.trim() || null,
        contactEmail: form.contactEmail?.trim() || null,
        contactPhone: form.contactPhone?.trim() || null,
        notes: form.notes?.trim() || null,
      });
      return submitNow ? post<FacilityRequestDto>(`/platform/facility-requests/${created.id}/submit`) : created;
    },
    onSuccess: () => {
      toast.success(submitNow ? 'Request sent to the Owner' : 'Draft saved');
      void queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not save the request', describeError(error)),
  });

  const ready = form.name.trim().length >= 2 && form.planIds.length > 0;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(EMPTY_REQUEST);

  return (
    <FormScreen
      title="New facility request"
      description={`A draft is kept for ${FACILITY_REQUEST_DRAFT_DAYS} days; the Owner creates the facility from it.`}
      icon={ClipboardList}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title="Facility" icon={Building2}>
        <Field label="Facility name" required>
          <Input value={form.name} onChange={(event) => set('name', event.target.value)} />
        </Field>
        <Field label="Environment">
          <ChoiceButtons
            value={form.environment}
            onChange={(value) => set('environment', value)}
            options={[
              { value: 'live', label: 'Live' },
              { value: 'demo', label: 'Demo' },
            ]}
          />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          checked={form.offlineEnabled}
          onCheckedChange={(value) => set('offlineEnabled', value)}
          label="Runs on its own server (offline mode)"
        />
      </FormSection>

      <FormSection title="Packages" icon={Package} columns={1}>
        <Field label="Packages" required hint="Choose every package this facility will use.">
          <PlanPicker plans={plans.data} chosen={form.planIds} onChange={(next) => set('planIds', next)} />
        </Field>
      </FormSection>

      <FormSection title="Contact" icon={Contact} columns={3}>
        <Field label="Contact name">
          <Input value={form.contactName ?? ''} onChange={(event) => set('contactName', event.target.value)} />
        </Field>
        <Field label="Contact email">
          <Input type="email" value={form.contactEmail ?? ''} onChange={(event) => set('contactEmail', event.target.value)} />
        </Field>
        <Field label="Contact phone">
          <Input value={form.contactPhone ?? ''} onChange={(event) => set('contactPhone', event.target.value)} />
        </Field>
      </FormSection>

      <FormSection title="For the Owner" icon={Send} columns={1}>
        <Field label="Notes for the Owner">
          <Textarea rows={3} value={form.notes ?? ''} onChange={(event) => set('notes', event.target.value)} />
        </Field>
        <Checkbox checked={submitNow} onCheckedChange={setSubmitNow} label="Send to the Owner now (untick to keep it as a draft)" />
      </FormSection>

      <FormActions submitLabel={submitNow ? 'Send request' : 'Save draft'} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
